/**
 * Banner Ad Component
 * Full-width background advertisement for contest pages
 */
export interface BannerAdComponentProps {
  /** Ad image URL from ad service */
  imageUrl?: string;
  /** Click URL for the ad */
  clickUrl?: string;
  /** Alt text for the ad image */
  altText?: string;
  /** Ad slot identifier */
  slotId?: string;
  /** Click handler (optional, defaults to opening clickUrl) */
  onClick?: () => void;
}

/**
 * Banner Ad Component
 * Displays a full-width background advertisement
 * Used in RulesScreen and other contest pages
 */
export default function BannerAdComponent({
  imageUrl,
  clickUrl,
  altText = 'Advertisement',
  slotId,
  onClick,
}: BannerAdComponentProps) {
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (clickUrl) {
      window.open(clickUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      onClick={imageUrl || clickUrl ? handleClick : undefined}
      className={`w-full h-full absolute inset-0 ${imageUrl || clickUrl ? 'cursor-pointer' : ''}`}
      role="banner"
      aria-label={altText}
      data-ad-slot={slotId || 'banner-ad'}
    >
      {imageUrl ? (
        <img src={imageUrl} alt={altText} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-zinc-800/50 flex items-center justify-center">
          <div className="text-center">
            <p className="text-zinc-400 text-sm">Banner Ad</p>
            <p className="text-zinc-500 text-xs">Background Image</p>
          </div>
        </div>
      )}
    </div>
  );
}
